import { Link } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    AiOutlineReload,
    AiOutlineEye,
    AiOutlineEyeInvisible,
    AiOutlineDisconnect,
    AiOutlineLink,
} from 'react-icons/ai';
import { FaFacebook } from 'react-icons/fa';
import myAxios from '../AxiosUrl';
import TrendChart from './TrendChart';
import styles from './MetaAnalytics.module.css';

const PRESETS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7d', label: 'Last 7 days' },
    { value: 'last_14d', label: 'Last 14 days' },
    { value: 'last_30d', label: 'Last 30 days' },
    { value: 'this_month', label: 'This month' },
    { value: 'last_month', label: 'Last month' },
    { value: 'last_90d', label: 'Last 90 days' },
];

const OBJECTIVES = {
    OUTCOME_SALES: 'Sales',
    OUTCOME_LEADS: 'Leads',
    OUTCOME_ENGAGEMENT: 'Engagement',
    OUTCOME_TRAFFIC: 'Traffic',
    OUTCOME_AWARENESS: 'Awareness',
    OUTCOME_APP_PROMOTION: 'App promotion',
    CONVERSIONS: 'Conversions',
    MESSAGES: 'Messages',
    LINK_CLICKS: 'Link clicks',
    POST_ENGAGEMENT: 'Post engagement',
    LEAD_GENERATION: 'Lead generation',
    REACH: 'Reach',
    VIDEO_VIEWS: 'Video views',
};

const ACCOUNT_STATUS = {
    1: 'Active',
    2: 'Disabled',
    3: 'Unsettled',
    7: 'Pending risk review',
    8: 'Pending settlement',
    9: 'In grace period',
    100: 'Pending closure',
    101: 'Closed',
};

// Friendly names for the result types we surface in "Results breakdown".
// Anything not listed here is a Meta internal duplicate and is hidden.
const ACTION_NAMES = {
    omni_purchase: 'Purchases',
    lead: 'Leads',
    'onsite_conversion.messaging_conversation_started_7d': 'Messaging conversations started',
    'onsite_conversion.messaging_conversation_replied_7d': 'Messaging conversations replied',
    'onsite_conversion.messaging_first_reply': 'First replies in Messenger',
    'onsite_conversion.messaging_user_call_placed': 'Calls placed from Messenger',
    link_click: 'Link clicks',
    landing_page_view: 'Landing page views',
    omni_add_to_cart: 'Add to cart',
    omni_initiated_checkout: 'Checkout initiated',
    omni_view_content: 'Content views',
    post_engagement: 'Post engagement',
    video_view: 'Video views (3s)',
    post_reaction: 'Post reactions',
    comment: 'Comments',
    like: 'Page likes',
    post: 'Shares',
    'onsite_conversion.post_save': 'Post saves',
    photo_view: 'Photo views',
};

const fmtNum = (v, digits = 0) =>
    Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 });

const fmtMoney = (v, currency = 'USD', digits = 2) => {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            maximumFractionDigits: digits,
        }).format(Number(v || 0));
    } catch (e) {
        return `${currency} ${Number(v || 0).toFixed(digits)}`;
    }
};

const fmtDate = (v) => (v ? new Date(v).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const humanObjective = (o) =>
    OBJECTIVES[o] ||
    String(o || '')
        .replace(/^OUTCOME_/, '')
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase());

const statusClass = (s) => {
    if (s === 'ACTIVE') return styles.pillGreen;
    if (s === 'PAUSED' || s === 'CAMPAIGN_PAUSED' || s === 'ADSET_PAUSED') return styles.pillGrey;
    if (s === 'ARCHIVED' || s === 'DELETED') return styles.pillGrey;
    return styles.pillYellow;
};

const humanStatus = (s) =>
    String(s || '')
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase());

function MetaAnalytics() {
    const [settings, setSettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [overviewLoading, setOverviewLoading] = useState(false);
    const [overviewError, setOverviewError] = useState('');
    const [preset, setPreset] = useState('last_30d');

    const [tokenInput, setTokenInput] = useState('');
    const [showToken, setShowToken] = useState(false);
    const [showTokenForm, setShowTokenForm] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [selecting, setSelecting] = useState(false);

    const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

    const loadSettings = useCallback(async () => {
        try {
            setSettingsLoading(true);
            const res = await myAxios.get('meta-analytics/settings');
            setSettings(res.data?.settings || { connected: false });
        } catch (err) {
            setSettings({ connected: false });
            toast.error(errMsg(err, 'Could not load Meta settings'));
        } finally {
            setSettingsLoading(false);
        }
    }, []);

    const loadOverview = useCallback(
        async (refresh = false) => {
            try {
                setOverviewLoading(true);
                setOverviewError('');
                const res = await myAxios.get('meta-analytics/overview', {
                    params: { preset, ...(refresh ? { refresh: 1 } : {}) },
                });
                setOverview(res.data?.overview || null);
            } catch (err) {
                setOverview(null);
                setOverviewError(errMsg(err, 'Could not load Meta data'));
            } finally {
                setOverviewLoading(false);
            }
        },
        [preset]
    );

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    useEffect(() => {
        if (settings?.connected) loadOverview(false);
        else setOverview(null);
    }, [settings?.connected, settings?.adAccountId, settings?.pageId, loadOverview]);

    const connect = async (e) => {
        e.preventDefault();
        const token = tokenInput.trim();
        if (!token) return toast.error('Paste your Meta access token first');
        try {
            setConnecting(true);
            const res = await myAxios.post('meta-analytics/token', { accessToken: token });
            setSettings(res.data?.settings || { connected: false });
            setTokenInput('');
            setShowToken(false);
            setShowTokenForm(false);
            toast.success('Meta connected');
        } catch (err) {
            toast.error(errMsg(err, 'Meta rejected the token'));
        } finally {
            setConnecting(false);
        }
    };

    const disconnect = async () => {
        if (!window.confirm('Remove the saved Meta token? You can connect again any time.')) return;
        try {
            await myAxios.delete('meta-analytics/token');
            setSettings({ connected: false });
            setOverview(null);
            toast.success('Meta disconnected');
        } catch (err) {
            toast.error(errMsg(err, 'Could not disconnect'));
        }
    };

    const select = async (patch) => {
        try {
            setSelecting(true);
            const res = await myAxios.post('meta-analytics/select', patch);
            setSettings(res.data?.settings || settings);
        } catch (err) {
            toast.error(errMsg(err, 'Could not save selection'));
        } finally {
            setSelecting(false);
        }
    };

    const connected = !!settings?.connected;
    const currency = overview?.account?.currency || settings?.adAccounts?.find((a) => a.id === settings?.adAccountId)?.currency || 'USD';
    const t = overview?.totals;
    const presetLabel = PRESETS.find((p) => p.value === preset)?.label || preset;

    const metricCards = t
        ? [
              { label: 'Amount spent', value: fmtMoney(t.spend, currency), sub: `${presetLabel}` },
              { label: 'Impressions', value: fmtNum(t.impressions), sub: `Frequency ${fmtNum(t.frequency, 2)}` },
              { label: 'Reach', value: fmtNum(t.reach), sub: 'Unique people' },
              { label: 'Clicks (all)', value: fmtNum(t.clicks), sub: `CTR ${fmtNum(t.ctr, 2)}%` },
              { label: 'Link clicks', value: fmtNum(t.inlineLinkClicks || t.linkClicks), sub: `Landing page views ${fmtNum(t.landingPageViews)}` },
              { label: 'Cost per click', value: fmtMoney(t.cpc, currency, 4), sub: `CPM ${fmtMoney(t.cpm, currency)}` },
              {
                  label: 'Purchases',
                  value: fmtNum(t.purchases),
                  sub: t.purchaseValue ? `Value ${fmtMoney(t.purchaseValue, currency)} · ROAS ${fmtNum(t.roas, 2)}x` : 'No purchase value reported',
                  accent: true,
              },
              { label: 'Leads', value: fmtNum(t.leads), sub: t.leads && t.spend ? `Cost per lead ${fmtMoney(t.spend / t.leads, currency)}` : '—' },
              {
                  label: 'Messaging conversations',
                  value: fmtNum(t.messagingStarted),
                  sub: `${fmtNum(t.messagingReplied)} replied`,
              },
              { label: 'Add to cart', value: fmtNum(t.addToCart), sub: `${fmtNum(t.initiateCheckout)} checkouts started` },
              { label: 'Post engagement', value: fmtNum(t.postEngagement), sub: `${fmtNum(t.reactions)} reactions · ${fmtNum(t.comments)} comments` },
              { label: 'Video views', value: fmtNum(t.videoViews), sub: `${fmtNum(t.pageLikes)} new page likes` },
          ]
        : [];

    const breakdown = (overview?.allActions || [])
        .filter((a) => ACTION_NAMES[a.type] && a.value > 0)
        .slice(0, 10);
    const breakdownMax = breakdown.length ? breakdown[0].value : 1;

    const tokenForm = (
        <form className={styles.tokenForm} onSubmit={connect}>
            <label className={styles.label} htmlFor="meta-token">
                Meta access token
            </label>
            <div className={styles.tokenRow}>
                <input
                    id="meta-token"
                    className={styles.input}
                    type={showToken ? 'text' : 'password'}
                    autoComplete="off"
                    spellCheck="false"
                    placeholder="EAAB..."
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                />
                <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => setShowToken((s) => !s)}
                    title={showToken ? 'Hide token' : 'Show token'}
                >
                    {showToken ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                </button>
                <button type="submit" className={styles.btnPrimary} disabled={connecting}>
                    {connecting ? 'Checking…' : connected ? 'Replace token' : 'Connect'}
                </button>
                {connected && (
                    <button type="button" className={styles.btnSecondary} onClick={() => setShowTokenForm(false)}>
                        Cancel
                    </button>
                )}
            </div>
            <p className={styles.hint}>
                We check the token with Meta, then store it encrypted. It is only used to read your ad and page reports.
            </p>
        </form>
    );

    return (
        <div className={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} />

            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.headerIcon}>
                        <FaFacebook />
                    </div>
                    <div>
                        <h1 className={styles.title}>Meta Analytics</h1>
                        <p className={styles.subtitle}>
                            Ad spend, results and page growth from your Meta account ·{' '}
                            <Link to="/chat-automation" style={{ color: '#2c6ecb' }}>Messenger & WhatsApp automation →</Link>
                        </p>
                    </div>
                </div>
                {connected && (
                    <div className={styles.headerActions}>
                        <select className={styles.select} value={preset} onChange={(e) => setPreset(e.target.value)}>
                            {PRESETS.map((p) => (
                                <option key={p.value} value={p.value}>
                                    {p.label}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={() => loadOverview(true)}
                            disabled={overviewLoading}
                            title="Fetch fresh numbers from Meta"
                        >
                            <AiOutlineReload className={overviewLoading ? styles.spin : ''} /> Refresh
                        </button>
                    </div>
                )}
            </div>

            {settingsLoading ? (
                <div className={styles.card}>
                    <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
                    <div className={`${styles.skeleton} ${styles.skeletonLine}`} style={{ width: '60%' }} />
                </div>
            ) : !connected ? (
                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>Connect your Meta account</h2>
                    <p className={styles.text}>
                        Paste an access token from your Meta Business account. The token must be able to read the ad account
                        and Facebook page you want to see here.
                    </p>
                    <ol className={styles.steps}>
                        <li>
                            Open <strong>Meta Business Suite → Settings → Users → System users</strong> and add (or pick) a
                            system user.
                        </li>
                        <li>
                            Under <strong>Assign assets</strong>, give it your <strong>ad account</strong> and{' '}
                            <strong>page</strong>.
                        </li>
                        <li>
                            Click <strong>Generate token</strong>, choose your app, set expiry to <strong>Never</strong>, and
                            tick <code>ads_read</code>, <code>pages_read_engagement</code> and <code>business_management</code>.
                        </li>
                        <li>Copy the token and paste it below.</li>
                    </ol>
                    {tokenForm}
                    <a
                        className={styles.link}
                        href="https://business.facebook.com/settings/system-users"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <AiOutlineLink /> Open Meta Business system users
                    </a>
                </div>
            ) : (
                <>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h2 className={styles.cardTitle}>Connection</h2>
                            <div className={styles.cardActions}>
                                {!showTokenForm && (
                                    <button type="button" className={styles.btnSecondary} onClick={() => setShowTokenForm(true)}>
                                        Replace token
                                    </button>
                                )}
                                <button type="button" className={styles.btnDanger} onClick={disconnect}>
                                    <AiOutlineDisconnect /> Disconnect
                                </button>
                            </div>
                        </div>

                        {settings.lastError && <div className={styles.alertError}>{settings.lastError}</div>}
                        {!settings.hasAdsRead && (
                            <div className={styles.alertWarn}>
                                This token has no <code>ads_read</code> permission, so ad reports will not load. Generate a new
                                token with that permission.
                            </div>
                        )}

                        <div className={styles.connGrid}>
                            <div className={styles.connItem}>
                                <span className={styles.connLabel}>Token</span>
                                <span className={styles.connValue}>
                                    {settings.tokenName || '—'}{' '}
                                    <span className={styles.muted}>
                                        ({humanStatus(settings.tokenType)}
                                        {settings.appName ? ` · ${settings.appName}` : ''})
                                    </span>
                                </span>
                                <span className={styles.mono}>{settings.tokenMasked}</span>
                            </div>
                            <div className={styles.connItem}>
                                <span className={styles.connLabel}>Expires</span>
                                <span className={styles.connValue}>{settings.expiresAt ? fmtDate(settings.expiresAt) : 'Never'}</span>
                                <span className={styles.muted}>Verified {fmtDate(settings.lastVerifiedAt)}</span>
                            </div>
                            <div className={styles.connItem}>
                                <span className={styles.connLabel}>Ad account</span>
                                <select
                                    className={styles.select}
                                    value={settings.adAccountId || ''}
                                    disabled={selecting}
                                    onChange={(e) => select({ adAccountId: e.target.value })}
                                >
                                    <option value="">— none —</option>
                                    {(settings.adAccounts || []).map((a) => (
                                        <option key={a.id} value={a.id}>
                                            {a.name} ({a.account_id}) · {a.currency}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.connItem}>
                                <span className={styles.connLabel}>Facebook page</span>
                                <select
                                    className={styles.select}
                                    value={settings.pageId || ''}
                                    disabled={selecting}
                                    onChange={(e) => select({ pageId: e.target.value })}
                                >
                                    <option value="">— none —</option>
                                    {(settings.pages || []).map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.chips}>
                            {(settings.scopes || []).map((s) => (
                                <span
                                    key={s}
                                    className={`${styles.chip} ${
                                        s === 'ads_read' || s === 'pages_read_engagement' ? styles.chipOk : ''
                                    }`}
                                >
                                    {s}
                                </span>
                            ))}
                        </div>

                        {showTokenForm && tokenForm}
                    </div>

                    {overviewError && <div className={styles.alertError}>{overviewError}</div>}

                    {overviewLoading && !overview ? (
                        <div className={styles.metricsGrid}>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className={styles.metricCard}>
                                    <div className={`${styles.skeleton} ${styles.skeletonLabel}`} />
                                    <div className={`${styles.skeleton} ${styles.skeletonValue}`} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        overview && (
                            <>
                                {!settings.adAccountId ? (
                                    <div className={styles.card}>
                                        <p className={styles.text}>Pick an ad account above to see ad reports.</p>
                                    </div>
                                ) : (
                                    overview.account && (
                                        <>
                                            <div className={styles.accountStrip}>
                                                <div>
                                                    <span className={styles.connLabel}>Ad account</span>
                                                    <span className={styles.connValue}>
                                                        {overview.account.name}{' '}
                                                        <span className={styles.muted}>#{overview.account.account_id}</span>
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className={styles.connLabel}>Status</span>
                                                    <span className={`${styles.pill} ${overview.account.status === 1 ? styles.pillGreen : styles.pillYellow}`}>
                                                        {ACCOUNT_STATUS[overview.account.status] || `Status ${overview.account.status}`}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className={styles.connLabel}>Lifetime spend</span>
                                                    <span className={styles.connValue}>{fmtMoney(overview.account.amountSpentLifetime, currency)}</span>
                                                </div>
                                                <div>
                                                    <span className={styles.connLabel}>Unpaid balance</span>
                                                    <span className={styles.connValue}>{fmtMoney(overview.account.balance, currency)}</span>
                                                </div>
                                                <div>
                                                    <span className={styles.connLabel}>Reporting time zone</span>
                                                    <span className={styles.connValue}>{overview.account.timezone || '—'}</span>
                                                </div>
                                            </div>

                                            <div className={styles.metricsGrid}>
                                                {metricCards.map((m) => (
                                                    <div key={m.label} className={`${styles.metricCard} ${m.accent ? styles.metricAccent : ''}`}>
                                                        <span className={styles.metricLabel}>{m.label}</span>
                                                        <span className={styles.metricValue}>{m.value}</span>
                                                        <span className={styles.metricSub}>{m.sub}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className={styles.card}>
                                                <div className={styles.cardHeader}>
                                                    <h2 className={styles.cardTitle}>Daily spend and clicks</h2>
                                                    <span className={styles.muted}>{presetLabel}</span>
                                                </div>
                                                {overview.daily && overview.daily.length > 1 ? (
                                                    <div className={styles.chartBox}>
                                                        <TrendChart data={overview.daily} currency={currency} />
                                                    </div>
                                                ) : (
                                                    <p className={styles.text}>Pick a longer period to see the daily trend.</p>
                                                )}
                                            </div>

                                            <div className={styles.card}>
                                                <div className={styles.cardHeader}>
                                                    <h2 className={styles.cardTitle}>Campaigns</h2>
                                                    <span className={styles.muted}>
                                                        {overview.campaigns?.length || 0} campaigns · sorted by spend
                                                    </span>
                                                </div>
                                                {overview.campaigns && overview.campaigns.length ? (
                                                    <div className={styles.tableWrap}>
                                                        <table className={styles.table}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Campaign</th>
                                                                    <th>Status</th>
                                                                    <th>Objective</th>
                                                                    <th>Budget</th>
                                                                    <th className={styles.num}>Spend</th>
                                                                    <th className={styles.num}>Impressions</th>
                                                                    <th className={styles.num}>Reach</th>
                                                                    <th className={styles.num}>Clicks</th>
                                                                    <th className={styles.num}>CTR</th>
                                                                    <th className={styles.num}>Purchases</th>
                                                                    <th className={styles.num}>Leads</th>
                                                                    <th className={styles.num}>Messages</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {overview.campaigns.map((c) => (
                                                                    <tr key={c.id}>
                                                                        <td className={styles.nameCell} title={c.name}>
                                                                            {c.name}
                                                                        </td>
                                                                        <td>
                                                                            <span className={`${styles.pill} ${statusClass(c.effectiveStatus)}`}>
                                                                                {humanStatus(c.effectiveStatus)}
                                                                            </span>
                                                                        </td>
                                                                        <td>{humanObjective(c.objective)}</td>
                                                                        <td>
                                                                            {c.dailyBudget != null
                                                                                ? `${fmtMoney(c.dailyBudget, currency)}/day`
                                                                                : c.lifetimeBudget != null
                                                                                ? `${fmtMoney(c.lifetimeBudget, currency)} lifetime`
                                                                                : 'Ad set level'}
                                                                        </td>
                                                                        <td className={styles.num}>{fmtMoney(c.spend, currency)}</td>
                                                                        <td className={styles.num}>{fmtNum(c.impressions)}</td>
                                                                        <td className={styles.num}>{fmtNum(c.reach)}</td>
                                                                        <td className={styles.num}>{fmtNum(c.clicks)}</td>
                                                                        <td className={styles.num}>{fmtNum(c.ctr, 2)}%</td>
                                                                        <td className={styles.num}>{fmtNum(c.purchases)}</td>
                                                                        <td className={styles.num}>{fmtNum(c.leads)}</td>
                                                                        <td className={styles.num}>{fmtNum(c.messagingStarted)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <p className={styles.text}>No campaigns in this ad account.</p>
                                                )}
                                            </div>
                                        </>
                                    )
                                )}

                                <div className={styles.twoCol}>
                                    {settings.adAccountId && (
                                        <div className={styles.card}>
                                            <div className={styles.cardHeader}>
                                                <h2 className={styles.cardTitle}>Results breakdown</h2>
                                                <span className={styles.muted}>{presetLabel}</span>
                                            </div>
                                            {breakdown.length ? (
                                                <div className={styles.actionList}>
                                                    {breakdown.map((a) => (
                                                        <div key={a.type} className={styles.actionRow}>
                                                            <span className={styles.actionName}>{ACTION_NAMES[a.type]}</span>
                                                            <div className={styles.actionTrack}>
                                                                <div
                                                                    className={styles.actionBar}
                                                                    style={{ width: `${Math.max((a.value / breakdownMax) * 100, 2)}%` }}
                                                                />
                                                            </div>
                                                            <span className={styles.actionValue}>{fmtNum(a.value)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className={styles.text}>No results reported for this period.</p>
                                            )}
                                        </div>
                                    )}

                                    <div className={styles.card}>
                                        <div className={styles.cardHeader}>
                                            <h2 className={styles.cardTitle}>Facebook page</h2>
                                            {overview.page?.link && (
                                                <a className={styles.link} href={overview.page.link} target="_blank" rel="noreferrer">
                                                    <AiOutlineLink /> Open page
                                                </a>
                                            )}
                                        </div>
                                        {overview.page ? (
                                            <>
                                                <div className={styles.pageHead}>
                                                    <div className={styles.pageAvatar}>
                                                        <FaFacebook />
                                                    </div>
                                                    <div>
                                                        <div className={styles.pageName}>{overview.page.name}</div>
                                                        <div className={styles.muted}>{overview.page.category || 'Page'}</div>
                                                    </div>
                                                </div>
                                                <div className={styles.pageStats}>
                                                    <div className={styles.pageStat}>
                                                        <span className={styles.metricLabel}>Followers</span>
                                                        <span className={styles.metricValue}>{fmtNum(overview.page.followers)}</span>
                                                    </div>
                                                    {overview.page.insights && (
                                                        <>
                                                            <div className={styles.pageStat}>
                                                                <span className={styles.metricLabel}>Post engagements</span>
                                                                <span className={styles.metricValue}>{fmtNum(overview.page.insights.postEngagements)}</span>
                                                            </div>
                                                            <div className={styles.pageStat}>
                                                                <span className={styles.metricLabel}>Page views</span>
                                                                <span className={styles.metricValue}>{fmtNum(overview.page.insights.pageViews)}</span>
                                                            </div>
                                                            <div className={styles.pageStat}>
                                                                <span className={styles.metricLabel}>New follows</span>
                                                                <span className={styles.metricValue}>{fmtNum(overview.page.insights.follows)}</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                                {!overview.page.insights && (
                                                    <p className={styles.hint}>
                                                        Meta returned no page insights for this period. Page metrics need the token's
                                                        page to have <code>pages_read_engagement</code> and some activity.
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            <p className={styles.text}>Pick a Facebook page above to see follower counts.</p>
                                        )}
                                    </div>
                                </div>

                                <p className={styles.footnote}>
                                    Numbers come straight from Meta for the selected period, in the ad account's currency and
                                    time zone. Reports are cached for 5 minutes; use Refresh for the latest figures.
                                </p>
                            </>
                        )
                    )}
                </>
            )}
        </div>
    );
}

export default MetaAnalytics;
